/**
 * API Client — iTunes album art, Last.fm metadata, gradient fallback
 */

const LASTFM_BASE = 'https://ws.audioscrobbler.com/2.0/'
const ITUNES_BASE = 'https://itunes.apple.com/search'

export class APIClient {
  // ─── Album Art ────────────────────────────────────────────────────────────

  /**
   * Fetch album art from iTunes Search API (free, no auth, CORS-friendly).
   * Returns a high-res image URL or null.
   */
  static async getAlbumArt(artist, track) {
    try {
      const query = encodeURIComponent(`${artist} ${track}`)
      const res   = await fetch(`${ITUNES_BASE}?term=${query}&media=music&limit=1`)
      const data  = await res.json()
      if (data.resultCount > 0) {
        const art = data.results[0].artworkUrl100
        if (art) return art.replace('100x100bb', '600x600bb')
      }
    } catch (_) {}
    return null
  }

  /**
   * Generate a deterministic radial gradient from a filename.
   * Used as fallback when no album art is found.
   */
  static generateGradient(filename) {
    let hash = 0
    for (const c of filename) {
      hash = Math.imul(31, hash) + c.charCodeAt(0) | 0
    }
    const h1 = Math.abs(hash) % 360
    const h2 = (h1 + 150) % 360
    const h3 = (h1 + 230) % 360
    return [
      `radial-gradient(circle at 30% 30%, hsl(${h1},75%,40%) 0%, hsl(${h2},65%,20%) 60%, hsl(${h3},70%,10%) 100%)`,
      `hsl(${h1},75%,40%)`,   // dominant colour for glow
    ]
  }

  /**
   * Set vinyl disc background: try iTunes → gradient fallback
   */
  static async applyVinylArt(vinylEl, artist, track, filename) {
    const artUrl = await APIClient.getAlbumArt(artist, track)
    const [gradient, glowColor] = APIClient.generateGradient(filename)

    if (artUrl) {
      // Blend album art with a dark radial overlay so grooves stay visible
      vinylEl.style.backgroundImage =
        `radial-gradient(circle at 50% 50%, transparent 20%, rgba(0,0,0,0.6) 100%), url(${artUrl})`
      vinylEl.style.backgroundSize  = 'cover'
      vinylEl.dataset.glowColor     = glowColor
    } else {
      vinylEl.style.backgroundImage = gradient
      vinylEl.dataset.glowColor     = glowColor
    }

    // Update glow to match the dominant album colour
    vinylEl.style.setProperty('--vinyl-glow', glowColor + '80')
  }

  // ─── Filename parsing ─────────────────────────────────────────────────────

  /**
   * Parse "Artist - Track Name.ext" → {artist, track}
   * Falls back to {artist: 'Unknown', track: nameWithoutExt}
   */
  static parseFilename(filename) {
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '')
    const parts = nameWithoutExt.split(' - ')
    if (parts.length >= 2) {
      return {
        artist: parts[0].trim(),
        track:  parts.slice(1).join(' - ').trim(),
      }
    }
    return { artist: 'Unknown', track: nameWithoutExt }
  }

  // ─── Last.fm (optional metadata) ──────────────────────────────────────────

  static async getTrackInfo(artist, track) {
    try {
      const params = new URLSearchParams({
        method: 'track.getInfo',
        artist,
        track,
        format: 'json',
      })
      const res  = await fetch(`${LASTFM_BASE}?${params}`)
      const data = await res.json()
      if (data.track) {
        return {
          name:      data.track.name,
          artist:    data.track.artist.name,
          duration:  parseInt(data.track.duration) || 0,
          listeners: parseInt(data.track.listeners) || 0,
          url:       data.track.url,
        }
      }
    } catch (_) {}
    return null
  }

  static async getSimilarTracks(artist, track, limit = 5) {
    try {
      const params = new URLSearchParams({
        method: 'track.getSimilar',
        artist,
        track,
        limit,
        format: 'json',
      })
      const res  = await fetch(`${LASTFM_BASE}?${params}`)
      const data = await res.json()
      if (data.similartracks?.track) {
        return data.similartracks.track.map(t => ({
          name:   t.name,
          artist: t.artist.name,
          match:  parseFloat(t.match) || 0,
        }))
      }
    } catch (_) {}
    return []
  }
}
